<template>
  <div class="broker-card">
    <!-- Photo -->
    <div class="broker-card__photo-wrap">
      <img
        v-if="broker.photo && broker.photo.src_avatar"
        :src="broker.photo.src_avatar"
        :alt="broker.name"
        class="broker-card__photo"
      />
      <div v-else class="broker-card__photo-placeholder">
        <span class="broker-card__initials">{{
          getInitials(broker.name)
        }}</span>
      </div>
      <div
        v-if="broker.badge"
        class="broker-card__badge"
        :style="{ backgroundColor: badgeColor(broker.badge) }"
      >
        <v-icon color="#fff" size="18">{{ badgeIcon(broker.badge) }}</v-icon>
      </div>
      <div class="broker-card__name-overlay">
        <span class="broker-card__name">{{ broker.name }}</span>
        <!-- <span class="broker-card__total_leads"
          >Total lead: {{ broker.total_leads }}</span
        > -->
      </div>
    </div>

    <!-- Performance bar -->
    <div class="broker-card__perf">
      <span class="broker-card__perf-label">Desempeño</span>
      <div class="broker-card__perf-track">
        <div
          class="broker-card__perf-fill"
          :style="{ width: performancePct + '%' }"
        ></div>
        <span class="broker-card__perf-pct">{{ performancePct }} %</span>
      </div>
    </div>

    <!-- Sample label -->
    <div class="broker-card__sample">
      últimos {{ broker.leads_sample_size }} prospectos
    </div>

    <!-- Metric rows -->
    <div class="metric-row metric-row--gray">
      <span class="metric-row__label">Por perfilar</span>
      <div class="metric-row__values">
        <span
          class="metric-row__amount metric-row__amount--clickable"
          @click="openLeadsModal('to_contact')"
        >
          {{ broker.to_contact.amount }}
        </span>
        <span class="metric-row__pct">{{ broker.to_contact.pct }}%</span>
      </div>
    </div>

    <div class="metric-row metric-row--purple">
      <span class="metric-row__label">Activos</span>
      <div class="metric-row__values">
        <span
          class="metric-row__amount metric-row__amount--clickable"
          @click="openLeadsModal('actives')"
        >
          {{ broker.actives.amount }}
        </span>
        <span class="metric-row__pct">{{ broker.actives.pct }}%</span>
      </div>
    </div>

    <div class="metric-row metric-row--pink">
      <span class="metric-row__label">Recorrido</span>
      <div class="metric-row__values">
        <span
          class="metric-row__amount metric-row__amount--clickable"
          @click="openLeadsModal('recorrido_to_promesa')"
        >
          {{ broker.recorrido_to_promesa.amount }}
        </span>
        <span class="metric-row__pct"
          >{{ broker.recorrido_to_promesa.pct }}%</span
        >
      </div>
    </div>

    <div class="metric-row metric-row--green">
      <span class="metric-row__label">Oferta</span>
      <div class="metric-row__values">
        <span
          class="metric-row__amount metric-row__amount--clickable"
          @click="openLeadsModal('offer_to_promesa')"
        >
          {{ broker.offer_to_promesa.amount }}
        </span>
        <span class="metric-row__pct">{{ broker.offer_to_promesa.pct }}%</span>
      </div>
    </div>

    <div class="metric-row metric-row--blue">
      <span class="metric-row__label">Operación en proceso</span>
      <div class="metric-row__values">
        <span
          class="metric-row__amount metric-row__amount--clickable"
          @click="openLeadsModal('downpayment_to_promesa')"
        >
          {{ broker.downpayment_to_promesa.amount }}
        </span>
        <span class="metric-row__pct"
          >{{ broker.downpayment_to_promesa.pct }}%</span
        >
      </div>
    </div>

    <div class="metric-row metric-row--gold">
      <span class="metric-row__label">Cierre</span>
      <div class="metric-row__values">
        <span
          class="metric-row__amount metric-row__amount--clickable"
          @click="openLeadsModal('closing')"
        >
          {{ broker.closing.amount }}
        </span>
        <span class="metric-row__pct">{{ broker.closing.pct }}%</span>
      </div>
    </div>

    <div class="metric-row metric-row--black">
      <span class="metric-row__label">Descartados</span>
      <div class="metric-row__values">
        <span
          class="metric-row__amount metric-row__amount--clickable"
          @click="openLeadsModal('discarded')"
        >
          {{ broker.discarded.amount }}
        </span>
        <span class="metric-row__pct">{{ broker.discarded.pct }}%</span>
      </div>
    </div>

    <div class="metric-row metric-row--white">
      <span class="metric-row__label">Comentarios</span>
      <span class="metric-row__amount metric-row__amount--dark">{{
        broker.total_comments
      }}</span>
    </div>

    <!-- Modal de leads -->
    <ListLeads
      :leads="detail.leads"
      :dialog="detail.dialog"
      :loading="detail.loading"
      @setDialog="value => (detail.dialog = value)"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

export default {
  name: "BrokerCardPerformance",

  components: {
    ListLeads: () =>
      import("@/components/lead/components/simpleListLeadsModal/index.vue")
  },

  props: {
    broker: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      detail: {
        leads: [],
        dialog: false,
        loading: false
      }
    };
  },

  computed: {
    ...mapState({
      leadsListByContact: state => state.reports.leadListByComertialReport
    }),
    performancePct() {
      return this.broker.overdue?.pct || 0;
    }
  },

  methods: {
    ...mapActions({
      getListLeads: "reports/fetchListLeadsByDetailReport"
    }),

    getInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .slice(0, 2)
        .map(w => w[0])
        .join("")
        .toUpperCase();
    },

    badgeColor(badge) {
      if (badge === 1) return "#f5c518";
      if (badge === 2) return "#adb5bd";
      if (badge === 3) return "#cd7f32";
      return "#607d8b";
    },

    badgeIcon(badge) {
      if (badge === 1) return "mdi-trophy";
      if (badge === 2) return "mdi-medal";
      return "mdi-medal-outline";
    },

    async openLeadsModal(metricKey) {
      const metricData = this.broker[metricKey];
      const leadIds = metricData?.lead_ids || [];

      if (leadIds.length === 0) return;

      this.detail.loading = true;
      this.detail.dialog = true;

      await this.getListLeads({
        leadIds,
        typeReport: "movements"
      });

      this.detail.leads = this.leadsListByContact;
      this.detail.loading = false;
    }
  }
};
</script>

<style scoped>
.broker-card {
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  width: 100%;
}

/* ── Photo ── */
.broker-card__photo-wrap {
  position: relative;
  width: 100%;
  height: 10rem;
  background-color: #0d1b2a;
  flex-shrink: 0;
}

.broker-card__photo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: top center;
  display: block;
}

.broker-card__photo-placeholder {
  width: 100%;
  height: 100%;
  background-color: #1a3a5c;
  display: flex;
  align-items: center;
  justify-content: center;
}

.broker-card__initials {
  color: #fff;
  font-size: 28px;
  font-weight: 700;
}

.broker-card__name-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  padding: 20px 12px 8px;
  display: flex;
  align-items: center;
}

.broker-card__name {
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.broker-card__total_leads {
  color: #dadada;
  font-size: 12px;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Badge ── */
.broker-card__badge {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
  z-index: 2;
}

/* ── Performance bar ── */
.broker-card__perf {
  background: #fff;
  padding: 5px 10px;
  border-bottom: 1px solid #f0f2f5;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.broker-card__perf-label {
  font-size: 0.8rem;
  color: #607d8b;
  font-weight: 500;
}

.broker-card__perf-track {
  position: relative;
  width: 100%;
  height: 14px;
  background-color: #e0e6ed;
  border-radius: 999px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.broker-card__perf-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background-color: #1a3a5c;
  border-radius: 999px;
  transition: width 0.4s ease;
}

.broker-card__perf-pct {
  position: relative;
  z-index: 1;
  font-size: 12px;
  font-weight: 700;
  color: #0d1b2a;
  background-color: #fff;
  border-radius: 999px;
  padding: 1px 8px;
}

/* ── Sample label ── */
.broker-card__sample {
  background: #f0f2f5;
  padding: 2px 10px;
  font-size: 0.75rem;
  color: #607d8b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

/* ── Metric rows ── */
.metric-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border: 1px solid #cfd8dc;
  margin-bottom: -1px;
  min-height: 52px;
}

.metric-row__label {
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  color: #fff;
  margin-bottom: 4px;
  width: 100%;
}

.metric-row__values {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.metric-row__amount {
  font-size: 22px;
  font-weight: 700;
  text-align: center;
  line-height: 1;
  color: #fff;
}

.metric-row__amount--clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.metric-row__amount--clickable:hover {
  transform: scale(1.1);
}

.metric-row__amount--dark {
  color: #0d1b2a;
}

.metric-row__pct {
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  text-align: center;
}

/* ── Color variants ── */
.metric-row--gray {
  background-color: #9e9e9e;
}

.metric-row--purple {
  background-color: #7b1fa2;
}

.metric-row--pink {
  background-color: #e91e63;
}

.metric-row--green {
  background-color: #388e3c;
}

.metric-row--blue {
  background-color: #1565c0;
}

.metric-row--gold {
  background-color: #f9a825;
}

.metric-row--black {
  background-color: #212121;
}

.metric-row--white {
  background-color: #fff;
  border-top: 1px solid #f0f2f5;
}

.metric-row--white .metric-row__label {
  color: #0d1b2a;
}

.metric-row--white .metric-row__amount--dark {
  font-size: 20px;
  font-weight: 700;
  color: #0d1b2a;
}
</style>
